import { useEffect, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Grid from "@mui/material/Grid"
import Checkbox from "@mui/material/Checkbox"
import FormControlLabel from "@mui/material/FormControlLabel"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import { RULES, validateFields, hasErrors } from "../../fieldRules"
import MDNumberInput from "../MDNumberInput"
import BudgetTopUpSummary from "./BudgetTopUpSummary"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import { FISCAL_MONTH_OPTIONS, formatVND } from "../../constants"
import { useLazyGetBudgetLineRowsQuery } from "shared/redux/endpoints/procurementV2"
import { showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"

// Months-only options for create — unlike the filter dropdown, a new
// pro.budget row always belongs to one specific month (fiscalMonth is
// mandatory server-side); "Full year" is a client-side sum of 12 such rows,
// never a row you can create directly.
const CREATE_MONTH_OPTIONS = FISCAL_MONTH_OPTIONS.filter((o) => o.value !== "")

// Modal for a pro.budget row: Category × Center × Năm × Tháng (mandatory) ×
// Total budget × Note. If that month already has a row (often a 0-total one
// BE created when an order committed against it), BE adds the amount onto
// it, so Save first looks the row up and asks the user to confirm the top-up.
//
// Category can also be created inline via "+ Create new category" (same
// expand-below-the-field pattern as the Order Request form's "+ Create new
// batch") — picking one clears the other. When active, the budget row's
// categoryId is left blank in `onCreate`'s payload and a `newCategory`
// object is sent instead, so the caller can POST /budget-categories first
// and use the returned id for the budget itself.
const AddBudgetLineModal = ({
  open,
  onClose,
  categories = [],
  centers = [],
  yearOptions = [],
  defaultCenterId = "",
  defaultFiscalYear,
  defaultFiscalMonth = "",
  busy = false,
  onCreate,
}) => {
  const [categoryId, setCategoryId] = useState("")
  const [centerId, setCenterId] = useState("")
  const [fiscalYear, setFiscalYear] = useState("")
  const [fiscalMonth, setFiscalMonth] = useState("")
  const [total, setTotal] = useState("")
  const [note, setNote] = useState("")
  // A standing policy for the whole (category, center) pair, not just this
  // row — matches the backend's own default (true) when a pair has never
  // had it set before.
  const [accumulateUnspent, setAccumulateUnspent] = useState(true)
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryCode, setNewCategoryCode] = useState("")
  const [newCategoryName, setNewCategoryName] = useState("")
  const [newCategoryAssetClassCode, setNewCategoryAssetClassCode] = useState("")
  const [newCategorySunPlAccount, setNewCategorySunPlAccount] = useState("")
  const [newCategorySunBsAccount, setNewCategorySunBsAccount] = useState("")
  const [existingRow, setExistingRow] = useState(null)
  const [findRows, { isFetching: checking }] = useLazyGetBudgetLineRowsQuery()

  useEffect(() => {
    if (!open) return
    setExistingRow(null)
    setCategoryId("")
    setCenterId(defaultCenterId || "")
    setFiscalYear(defaultFiscalYear != null ? String(defaultFiscalYear) : "")
    // "Full year" (defaultFiscalMonth === "") isn't a valid create value —
    // leave the month unset so the user has to pick one.
    setFiscalMonth(defaultFiscalMonth || "")
    setTotal("")
    setNote("")
    setAccumulateUnspent(true)
    setShowNewCategory(false)
    setNewCategoryCode("")
    setNewCategoryName("")
    setNewCategoryAssetClassCode("")
    setNewCategorySunPlAccount("")
    setNewCategorySunBsAccount("")
    setTried(false)
  }, [open, defaultCenterId, defaultFiscalYear, defaultFiscalMonth])

  const categoryOptions = categories.map((c) => ({ value: String(c.id), label: c.name }))
  const centerOptions = centers.map((c) => ({ value: String(c.id), label: c.name }))
  const selectedCategory = categories.find((c) => String(c.id) === categoryId)

  const [tried, setTried] = useState(false)
  const allCategoryErrors = showNewCategory
    ? validateFields(
        {
          code: newCategoryCode,
          name: newCategoryName,
          assetClassCode: newCategoryAssetClassCode,
          sunPlAccount: newCategorySunPlAccount,
          sunBsAccount: newCategorySunBsAccount,
        },
        RULES.budgetCategory
      )
    : {}
  const categoryErrors = tried ? allCategoryErrors : {}

  const isValid =
    (showNewCategory ? newCategoryName.trim() : categoryId) &&
    centerId &&
    fiscalYear &&
    fiscalMonth &&
    Number(total) >= 0

  const buildPayload = (row) => ({
      categoryId: showNewCategory ? undefined : categoryId,
      newCategory: showNewCategory
        ? {
            code: newCategoryCode.trim() || undefined,
            name: newCategoryName.trim(),
            assetClassCode: newCategoryAssetClassCode.trim() || undefined,
            sunPlAccount: newCategorySunPlAccount.trim() || undefined,
            sunBsAccount: newCategorySunBsAccount.trim() || undefined,
          }
        : undefined,
      centerId,
      fiscalYear: Number(fiscalYear),
      fiscalMonth: Number(fiscalMonth),
      total: Number(total) || 0,
      note: note.trim() || undefined,
      accumulateUnspent: row ? undefined : accumulateUnspent,
    })

  const handleSubmit = async () => {
    if (!isValid) return
    setTried(true)
    if (hasErrors(allCategoryErrors)) return
    if (!showNewCategory) {
      try {
        const rows = await findRows({
          categoryId,
          centerId,
          fiscalYear: Number(fiscalYear),
          fiscalMonth: Number(fiscalMonth),
        }).unwrap()
        const row = rows.find((r) => Number(r.fiscalMonth) === Number(fiscalMonth))
        if (row) {
          setExistingRow(row)
          return
        }
      } catch (e) {
        // An empty month comes back as 200 + []; a 404 is treated the same way.
        if (!e?.notFound) {
          showToastError(getApiErrorMessage(e, "Couldn't check this month's budget. Please try again."))
          return
        }
      }
    }
    onCreate(buildPayload(null), null)
  }

  const amount = Number(total) || 0
  const periodLabel = `${String(fiscalMonth).padStart(2, "0")}/${fiscalYear}`

  if (existingRow) {
    return (
      <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
        <DialogTitle>Add to existing budget?</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" fontWeight="regular" display="block" mb={2}>
            {existingRow.centerName} · {existingRow.categoryName} already has a budget for {periodLabel}
            {existingRow.total === 0 && " (no budget allocated yet — created from orders)"}. Saving adds{" "}
            <strong>{formatVND(amount)}</strong> to it.
          </MDTypography>
          <BudgetTopUpSummary row={existingRow} amount={amount} />
          {accumulateUnspent !== existingRow.accumulateUnspent && (
            <MDTypography variant="caption" color="text" display="block" mt={1.5}>
              The accumulate setting stays {existingRow.accumulateUnspent ? "on" : "off"} for this pair — it
              isn&apos;t changed when adding to an existing month.
            </MDTypography>
          )}
          {note.trim() && (
            <MDTypography variant="caption" color="text" display="block" mt={1}>
              Your note will replace the existing note{existingRow.note ? ` (“${existingRow.note}”)` : ""}.
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setExistingRow(null)} disabled={busy}>
            Back
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => onCreate(buildPayload(existingRow), existingRow)}
            disabled={busy}
          >
            {busy ? "Saving…" : `Add ${formatVND(amount)}`}
          </MDButton>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Add budget line</DialogTitle>
      <DialogContent>
        <MDBox pt={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDSelect
                required={!showNewCategory}
                labelType="non-sticky"
                labelText="Category"
                options={categoryOptions}
                value={categoryOptions.find((o) => o.value === categoryId) || null}
                onChange={(o) => setCategoryId(o?.value || "")}
                isDisabled={busy || showNewCategory}
              />
              {!showNewCategory ? (
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  style={{ marginTop: 6 }}
                  disabled={busy}
                  onClick={() => {
                    setShowNewCategory(true)
                    setCategoryId("")
                  }}
                >
                  + Create new category
                </MDButton>
              ) : (
                <MDBox mt={1.5} p={1.5} borderRadius="lg" bgColor="grey-100">
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        labelType="non-sticky"
                        labelText="Code"
                        value={newCategoryCode}
                        error={!!categoryErrors.code}
                        helperText={categoryErrors.code}
                        onChange={(e) => setNewCategoryCode(e.target.value)}
                        disabled={busy}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <MDInput
                        fullWidth
                        required
                        labelType="non-sticky"
                        labelText="New category name"
                        value={newCategoryName}
                        error={!!categoryErrors.name}
                        helperText={categoryErrors.name}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        disabled={busy}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        labelType="non-sticky"
                        labelText="Asset class code"
                        value={newCategoryAssetClassCode}
                        error={!!categoryErrors.assetClassCode}
                        helperText={categoryErrors.assetClassCode}
                        onChange={(e) => setNewCategoryAssetClassCode(e.target.value)}
                        disabled={busy}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        labelType="non-sticky"
                        labelText="SUN P&L account"
                        value={newCategorySunPlAccount}
                        error={!!categoryErrors.sunPlAccount}
                        helperText={categoryErrors.sunPlAccount}
                        onChange={(e) => setNewCategorySunPlAccount(e.target.value)}
                        disabled={busy}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MDInput
                        fullWidth
                        labelType="non-sticky"
                        labelText="SUN BS account"
                        value={newCategorySunBsAccount}
                        error={!!categoryErrors.sunBsAccount}
                        helperText={categoryErrors.sunBsAccount}
                        onChange={(e) => setNewCategorySunBsAccount(e.target.value)}
                        disabled={busy}
                      />
                    </Grid>
                  </Grid>
                  <MDButton
                    variant="text"
                    color="secondary"
                    size="small"
                    style={{ marginTop: 8 }}
                    disabled={busy}
                    onClick={() => {
                      setShowNewCategory(false)
                      setNewCategoryCode("")
                      setNewCategoryName("")
                      setNewCategoryAssetClassCode("")
                      setNewCategorySunPlAccount("")
                      setNewCategorySunBsAccount("")
                    }}
                  >
                    Cancel
                  </MDButton>
                </MDBox>
              )}
            </Grid>

            {!showNewCategory && selectedCategory && (
              <Grid item xs={12}>
                <MDBox p={1.5} borderRadius="lg" bgColor="grey-100">
                  <MDTypography variant="caption" color="text">
                    Group: <strong>{selectedCategory.assetClassCode || "—"}</strong> · Account code:{" "}
                    <strong>{selectedCategory.sunPlAccount || "—"}</strong>
                  </MDTypography>
                </MDBox>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Center"
                options={centerOptions}
                value={centerOptions.find((o) => o.value === centerId) || null}
                onChange={(o) => setCenterId(o?.value || "")}
                isDisabled={busy}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Year"
                options={yearOptions}
                value={yearOptions.find((o) => o.value === fiscalYear) || null}
                onChange={(o) => setFiscalYear(o?.value || "")}
                isDisabled={busy}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MDSelect
                required
                labelType="non-sticky"
                labelText="Month"
                options={CREATE_MONTH_OPTIONS}
                value={CREATE_MONTH_OPTIONS.find((o) => o.value === fiscalMonth) || null}
                onChange={(o) => setFiscalMonth(o?.value ?? "")}
                isDisabled={busy}
              />
            </Grid>

            <Grid item xs={12}>
              <MDNumberInput
                fullWidth
                required
                labelType="non-sticky"
                labelText="Total budget"
                value={total}
                onChange={setTotal}
                disabled={busy}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={accumulateUnspent}
                    onChange={(e) => setAccumulateUnspent(e.target.checked)}
                    disabled={busy}
                  />
                }
                label={
                  <MDTypography variant="button" fontWeight="regular">
                    Accumulate unspent budget into future months
                  </MDTypography>
                }
              />
              <MDTypography variant="caption" color="text" display="block" sx={{ mt: -0.5 }}>
                A standing policy for this whole category/center pair, not just this row — unspent
                budget keeps rolling forward month to month while this is on. Unchecking it here turns
                the policy off for every future month of this pair too, not just this one.
              </MDTypography>
            </Grid>

            <Grid item xs={12}>
              <MDInput
                fullWidth
                multiline
                rows={2}
                labelType="non-sticky"
                labelText="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
              />
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={busy || checking || !isValid}>
          {busy || checking ? "Saving…" : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}

export default AddBudgetLineModal
